import type { SessionConfigOption } from "@agentclientprotocol/sdk";
import type { AcpClient } from "../../acp/client.js";
import {
  extractAcpError,
  formatErrorMessage,
  isAcpQueryClosedBeforeResponseError,
  isAcpResourceNotFoundError,
} from "../../acp/error-normalization.js";
import { assertRequestedModelSupported, type SessionModelState } from "../../acp/model-support.js";
import {
  assertControlAuthority,
  InterruptedError,
  TimeoutError,
  withTimeout,
  type AcpControlAuthority,
} from "../../async-control.js";
import {
  AgentDisconnectedError,
  SessionConfigOptionReplayError,
  SessionModeReplayError,
  SessionModelReplayError,
  SessionResumeRequiredError,
} from "../../errors.js";
import { incrementPerfCounter } from "../../perf-metrics.js";
import { isProcessAlive } from "../../process-liveness.js";
import {
  applyConfigOptionsToRecord,
  applyConfigOptionSelection,
  applyModelSelection,
} from "../../session/config-options.js";
import { cloneSessionAcpxState } from "../../session/conversation-model.js";
import {
  getDesiredConfigOptions,
  getDesiredModeId,
  getDesiredModelId,
  syncAdvertisedModelState,
} from "../../session/mode-preference.js";
import {
  advertisedModelState,
  clearAdvertisedModelState,
  removeModelConfigOptions,
} from "../../session/model-state.js";
import type { SessionRecord, SessionResumePolicy } from "../../types.js";
import {
  applyLifecycleSnapshotToRecord,
  reconcileAgentSessionId,
  sessionHasAgentMessages,
} from "./lifecycle.js";
import {
  mergeSessionOptions,
  sessionOptionsFromRecord,
  type SessionAgentOptions,
} from "./session-options.js";

export type ConnectedSessionController = {
  hasActivePrompt: () => boolean;
  requestCancelActivePrompt: () => Promise<boolean>;
  setSessionMode: (modeId: string) => Promise<void>;
  setSessionModel: (modelId: string) => ReturnType<AcpClient["setSessionModel"]>;
  setSessionConfigOption: (
    configId: string,
    value: string,
  ) => ReturnType<AcpClient["setSessionConfigOption"]>;
};

export type ConnectAndLoadSessionOptions = {
  client: AcpClient;
  record: SessionRecord;
  resumePolicy?: SessionResumePolicy;
  sessionOptions?: SessionAgentOptions;
  replacingMode?: true;
  replacingConfigOption?: {
    key: string;
    resolve?: (record: SessionRecord) => string;
  };
  timeoutMs?: number;
  authority?: AcpControlAuthority;
  verbose?: boolean;
  suppressWarnings?: boolean;
  activeController: ConnectedSessionController;
  onClientAvailable?: (controller: ConnectedSessionController) => void;
  onConnectedRecord?: (record: SessionRecord) => void;
  onSessionIdResolved?: (sessionId: string) => void;
};

export type ConnectAndLoadSessionResult = {
  sessionId: string;
  agentSessionId?: string;
  resumed: boolean;
  loadError?: string;
};

const SESSION_LOAD_UNSUPPORTED_CODES = new Set([-32601, -32602]);

type ReplayState = {
  authority?: AcpControlAuthority;
  acknowledged: boolean;
  isAuthorityFailure: (error: unknown) => boolean;
};

function createReplayState(authority?: AcpControlAuthority): ReplayState {
  let failure: { error: unknown } | undefined;
  return {
    acknowledged: false,
    authority: authority
      ? {
          assertActive() {
            try {
              assertControlAuthority(authority);
            } catch (error) {
              failure = { error };
              throw error;
            }
          },
        }
      : undefined,
    isAuthorityFailure: (error) => failure !== undefined && failure.error === error,
  };
}

function rethrowAuthorityFailure(replay: ReplayState, error: unknown): void {
  if (replay.isAuthorityFailure(error)) {
    throw error;
  }
}

function shouldFallbackToNewSession(error: unknown, record: SessionRecord): boolean {
  if (isHardReconnectFailure(error)) {
    return false;
  }
  const acp = extractAcpError(error);
  if (isAcpResourceNotFoundError(error) || isUnsupportedSessionLoadAcpError(acp)) {
    return true;
  }

  return !sessionHasAgentMessages(record) && isFallbackSafeEmptySessionError(error, acp);
}

function isHardReconnectFailure(error: unknown): boolean {
  return error instanceof TimeoutError || error instanceof InterruptedError;
}

function isUnsupportedSessionLoadAcpError(acp: ReturnType<typeof extractAcpError>): boolean {
  return !!acp && SESSION_LOAD_UNSUPPORTED_CODES.has(acp.code);
}

function isFallbackSafeEmptySessionError(
  error: unknown,
  acp: ReturnType<typeof extractAcpError>,
): boolean {
  return isAcpQueryClosedBeforeResponseError(error) || acp?.code === -32603;
}

function requiresSameSession(resumePolicy: SessionResumePolicy | undefined): boolean {
  return resumePolicy === "same-session-only";
}

function makeSessionResumeRequiredError(params: {
  record: SessionRecord;
  reason: string;
  cause?: unknown;
}): SessionResumeRequiredError {
  return new SessionResumeRequiredError(
    `Persistent ACP session ${params.record.acpSessionId} could not be resumed: ${params.reason}`,
    {
      cause: params.cause instanceof Error ? params.cause : undefined,
    },
  );
}

async function replayDesiredMode(params: {
  client: AcpClient;
  record: SessionRecord;
  sessionId: string;
  desiredModeId: string | undefined;
  replay: ReplayState;
  previousSessionId: string;
  timeoutMs?: number;
  verbose?: boolean;
}): Promise<PreferenceReplayResult["modeMetadata"]> {
  if (!params.desiredModeId) {
    return undefined;
  }

  try {
    await withTimeout(
      params.client.setSessionMode(params.sessionId, params.desiredModeId, params.replay.authority),
      params.timeoutMs,
    );
    params.replay.acknowledged = true;
    if (params.verbose) {
      process.stderr.write(
        `[acpx] replayed desired mode ${params.desiredModeId} on fresh ACP session ${params.sessionId} (previous ${params.previousSessionId})\n`,
      );
    }
    // A received mode update may remove models; an absent catalog is authoritative.
    return {
      models: advertisedModelState(params.record.acpx),
      configOptionsPresent: params.record.acpx?.config_options !== undefined,
    };
  } catch (error) {
    rethrowAuthorityFailure(params.replay, error);
    throw new SessionModeReplayError(
      `Failed to replay saved session mode ${params.desiredModeId} on fresh ACP session ${params.sessionId}: ${formatErrorMessage(error)}`,
      {
        cause: error instanceof Error ? error : undefined,
        retryable: true,
      },
    );
  }
}

function canReplayModel(
  desiredModelId: string | undefined,
  models: SessionModelState | undefined,
  createdFreshSession: boolean,
): desiredModelId is string {
  // Resume metadata is optional; omission alone does not revoke saved model support.
  return Boolean(desiredModelId) && (createdFreshSession || models !== undefined);
}

async function replayDesiredModel(params: {
  client: AcpClient;
  sessionId: string;
  desiredModelId: string | undefined;
  replay: ReplayState;
  previousSessionId: string;
  record: SessionRecord;
  models: import("../../acp/client.js").SessionLoadResult["models"] | undefined;
  createdFreshSession: boolean;
  timeoutMs?: number;
  verbose?: boolean;
  suppressWarnings?: boolean;
}): Promise<ModelReplayResult> {
  if (!canReplayModel(params.desiredModelId, params.models, params.createdFreshSession)) {
    return { replayed: false };
  }

  try {
    const warning = assertRequestedModelSupported({
      requestedModel: params.desiredModelId,
      models: params.models,
      agentCommand: params.record.agentCommand,
      context: "replay",
    });
    emitModelSupportWarning(warning, params.suppressWarnings);
    if (!params.models) {
      return { replayed: false };
    }
    // Reassert even an unchanged model: its acknowledgement reconciles saved
    // sibling selections left invalid by earlier model switches.
    const response = await withTimeout(
      params.client.setSessionModel(
        params.sessionId,
        params.desiredModelId,
        params.models,
        params.replay.authority,
      ),
      params.timeoutMs,
    );
    params.replay.acknowledged = true;
    params.record.acpx = applyModelSelection(params.record.acpx, params.desiredModelId, response);
    const models = advertisedModelState(params.record.acpx);
    if (params.verbose) {
      process.stderr.write(
        `[acpx] replayed desired model ${params.desiredModelId} on ACP session ${params.sessionId} (previous ${params.previousSessionId})\n`,
      );
    }
    return {
      replayed: true,
      models,
      configOptions: response?.configOptions,
    };
  } catch (error) {
    rethrowAuthorityFailure(params.replay, error);
    throw new SessionModelReplayError(
      `Failed to replay saved session model ${params.desiredModelId} on ACP session ${params.sessionId}: ${formatErrorMessage(error)}`,
      {
        cause: error instanceof Error ? error : undefined,
        retryable: true,
      },
    );
  }
}

function emitModelSupportWarning(warning: string | undefined, suppressWarnings?: boolean): void {
  if (warning && !suppressWarnings) {
    process.stderr.write(`[acpx] warning: ${warning}\n`);
  }
}

type ModelReplayResult =
  | { replayed: false; configOptions?: undefined }
  | {
      replayed: true;
      models: SessionModelState | undefined;
      configOptions: SessionConfigOption[] | undefined;
    };

type ConfigReplayResult =
  | { replayed: false }
  | {
      replayed: true;
      models: SessionModelState | undefined;
    };

type PreferenceReplayResult = {
  modelReplay: ModelReplayResult;
  configReplay: ConfigReplayResult;
  modeMetadata?: {
    models: SessionModelState | undefined;
    configOptionsPresent: boolean;
  };
};

async function replayDesiredConfigOptions(params: {
  client: AcpClient;
  record: SessionRecord;
  sessionId: string;
  desiredConfigOptions: Record<string, string>;
  replay: ReplayState;
  acceptedConfigOptions?: SessionConfigOption[];
  replacingConfigOption?: ConnectAndLoadSessionOptions["replacingConfigOption"];
  previousSessionId: string;
  timeoutMs?: number;
  verbose?: boolean;
}): Promise<ConfigReplayResult> {
  let result: ConfigReplayResult = { replayed: false };
  let acceptedConfigOptions = params.acceptedConfigOptions;
  const replacingKey = resolveReplacingConfigOptionId(params.replacingConfigOption, params.record);
  for (const [configId, value] of Object.entries(params.desiredConfigOptions)) {
    // Each acknowledgement can retire later selections. Startup snapshots alone
    // must not replace saved preferences with the adapter's defaults.
    if (
      configId === replacingKey ||
      (acceptedConfigOptions &&
        !acceptsSavedConfigValue(
          acceptedConfigOptions.find((option) => option.id === configId),
          value,
        ))
    ) {
      continue;
    }
    try {
      const response = await withTimeout(
        params.client.setSessionConfigOption(
          params.sessionId,
          configId,
          value,
          advertisedModelState(params.record.acpx),
          params.replay.authority,
        ),
        params.timeoutMs,
      );
      params.replay.acknowledged = true;
      params.record.acpx = applyConfigOptionSelection(
        params.record.acpx,
        configId,
        value,
        response,
      );
      acceptedConfigOptions = params.record.acpx.config_options;
      result = {
        replayed: true,
        models: advertisedModelState(params.record.acpx),
      };
      if (params.verbose) {
        process.stderr.write(
          `[acpx] replayed desired config option ${configId} on ACP session ${params.sessionId} (previous ${params.previousSessionId})\n`,
        );
      }
    } catch (error) {
      rethrowAuthorityFailure(params.replay, error);
      throw new SessionConfigOptionReplayError(
        `Failed to replay saved session config option ${configId} on ACP session ${params.sessionId}: ${formatErrorMessage(error)}`,
        {
          cause: error instanceof Error ? error : undefined,
          retryable: true,
        },
      );
    }
  }
  return result;
}

function restoreOriginalSessionState(params: {
  record: SessionRecord;
  sessionId: string;
  agentSessionId: string | undefined;
}): void {
  params.record.acpSessionId = params.sessionId;
  params.record.agentSessionId = params.agentSessionId;
}

export async function connectAndLoadSession(
  options: ConnectAndLoadSessionOptions,
): Promise<ConnectAndLoadSessionResult> {
  const record = options.record;
  const client = options.client;
  assertControlAuthority(options.authority);
  const sameSessionOnly = requiresSameSession(options.resumePolicy) || Boolean(record.importedFrom);
  const originalSessionId = record.acpSessionId;
  const originalAgentSessionId = record.agentSessionId;
  const originalAcpx = cloneSessionAcpxState(record.acpx);
  const desiredModeId = options.replacingMode ? undefined : getDesiredModeId(record.acpx);
  const desiredModelId = getDesiredModelId(record.acpx);
  const desiredConfigOptions = getDesiredConfigOptions(record.acpx);
  const storedProcessAlive = isProcessAlive(record.pid);
  const shouldReconnect = Boolean(record.pid) && !storedProcessAlive;

  logReconnectAttempt(record, storedProcessAlive, shouldReconnect, options.verbose);

  const reusingLoadedSession = client.hasReusableSession(record.acpSessionId);
  if (reusingLoadedSession) {
    incrementPerfCounter("runtime.connect_and_load.reused_session");
  } else {
    await withTimeout(
      client.start(options.authority, {
        sessionOptions: mergeSessionOptions(
          options.sessionOptions,
          sessionOptionsFromRecord(record),
        ),
      }),
      options.timeoutMs,
    );
  }
  assertControlAuthority(options.authority);
  options.onClientAvailable?.(options.activeController);
  applyLifecycleSnapshotToRecord(record, client.getAgentLifecycleSnapshot());
  record.closed = false;
  record.closedAt = undefined;
  options.onConnectedRecord?.(record);

  const loadState = await loadOrCreateRuntimeSession({
    client,
    record,
    reusingLoadedSession,
    sameSessionOnly,
    timeoutMs: options.timeoutMs,
    authority: options.authority,
  });
  const {
    resumed,
    loadError,
    sessionId,
    createdFreshSession,
    pendingAgentSessionId,
    sessionModels,
  } = loadState;

  applyReconnectedModelState(
    record,
    sessionModels,
    loadState.configOptionsPresent,
    loadState.legacyModelMetadataPresent,
    createdFreshSession,
  );

  const preferenceReplay = await replaySessionPreferences({
    client,
    record,
    createdFreshSession,
    reusingLoadedSession,
    replacingConfigOption: options.replacingConfigOption,
    sessionId,
    pendingAgentSessionId,
    originalSessionId,
    originalAgentSessionId,
    originalAcpx,
    desiredModeId,
    desiredModelId,
    desiredConfigOptions,
    sessionModels,
    timeoutMs: options.timeoutMs,
    verbose: options.verbose,
    suppressWarnings: options.suppressWarnings,
    authority: options.authority,
  });

  applyReconnectedModelState(
    record,
    resolveModelsAfterReplay(preferenceReplay, sessionModels),
    resolveConfigOptionsPresenceAfterReplay(preferenceReplay, loadState.configOptionsPresent),
    loadState.legacyModelMetadataPresent,
    createdFreshSession,
  );

  options.onSessionIdResolved?.(sessionId);

  return {
    sessionId,
    agentSessionId: record.agentSessionId,
    resumed,
    loadError,
  };
}

function resolveModelsAfterReplay(
  replay: PreferenceReplayResult,
  initialModels: SessionModelState | undefined,
): SessionModelState | undefined {
  const models = resolveModeReplayModels(replay.modeMetadata, initialModels);
  if (replay.configReplay.replayed) {
    return (
      replay.configReplay.models ??
      preserveLegacyModels(replay.modelReplay.replayed ? replay.modelReplay.models : models)
    );
  }
  return replay.modelReplay.replayed ? replay.modelReplay.models : models;
}

function resolveModeReplayModels(
  modeMetadata: PreferenceReplayResult["modeMetadata"],
  initialModels: SessionModelState | undefined,
): SessionModelState | undefined {
  return modeMetadata ? modeMetadata.models : initialModels;
}

function preserveLegacyModels(
  models: SessionModelState | undefined,
): SessionModelState | undefined {
  return models && !models.configId ? models : undefined;
}

function resolveConfigOptionsPresenceAfterReplay(
  replay: PreferenceReplayResult,
  initiallyPresent: boolean,
): boolean {
  return (
    initiallyPresent ||
    replay.modeMetadata?.configOptionsPresent === true ||
    replay.configReplay.replayed ||
    replay.modelReplay.configOptions !== undefined
  );
}

function applyReconnectedModelState(
  record: SessionRecord,
  sessionModels: import("../../acp/client.js").SessionLoadResult["models"],
  configOptionsPresent: boolean,
  legacyModelMetadataPresent: boolean,
  createdFreshSession: boolean,
): void {
  clearOmittedFreshSessionConfigOptions(record, createdFreshSession, configOptionsPresent);
  if (sessionModels) {
    if (legacyModelMetadataPresent && !sessionModels.configId && record.acpx) {
      removeModelConfigOptions(record.acpx);
    }
    syncAdvertisedModelState(record, sessionModels);
  } else {
    clearRemovedModelState(record, legacyModelMetadataPresent || createdFreshSession);
  }
}

function clearOmittedFreshSessionConfigOptions(
  record: SessionRecord,
  createdFreshSession: boolean,
  configOptionsPresent: boolean,
): void {
  if (createdFreshSession && !configOptionsPresent && record.acpx) {
    delete record.acpx.config_options;
  }
}

function clearRemovedModelState(record: SessionRecord, shouldClear: boolean): void {
  if (shouldClear && record.acpx) {
    clearAdvertisedModelState(record.acpx);
  }
}

function logReconnectAttempt(
  record: SessionRecord,
  storedProcessAlive: boolean,
  shouldReconnect: boolean,
  verbose: boolean | undefined,
): void {
  if (!verbose) {
    return;
  }
  if (storedProcessAlive) {
    process.stderr.write(
      `[acpx] saved session pid ${record.pid} is running; reconnecting to saved ACP session\n`,
    );
    return;
  }
  if (shouldReconnect) {
    process.stderr.write(
      `[acpx] saved session pid ${record.pid} is dead; respawning agent and attempting session reconnect\n`,
    );
  }
}

function replacesModel(
  replacingConfigOption: ConnectAndLoadSessionOptions["replacingConfigOption"],
  models: SessionModelState | undefined,
  originalAcpx: SessionRecord["acpx"],
): boolean {
  // Metadata may be omitted on resume; either advertisement identifies the
  // model replacement that must bypass obsolete model and config preferences.
  const key = replacingConfigOption?.key;
  return (
    key !== undefined &&
    (key === "model" ||
      key === models?.configId ||
      key === advertisedModelState(originalAcpx)?.configId)
  );
}

function acceptsSavedConfigValue(option: SessionConfigOption | undefined, value: string): boolean {
  if (!option || option.type !== "select") {
    return false;
  }
  return (
    option.currentValue === value ||
    option.options.some((entry) =>
      "options" in entry
        ? entry.options.some((choice) => choice.value === value)
        : entry.value === value,
    )
  );
}

function resolveReplacingConfigOptionId(
  replacement: ConnectAndLoadSessionOptions["replacingConfigOption"],
  record: SessionRecord,
): string | undefined {
  return replacement?.resolve?.(record) ?? replacement?.key;
}

type ReplaySessionPreferencesOptions = {
  client: AcpClient;
  record: SessionRecord;
  createdFreshSession: boolean;
  reusingLoadedSession: boolean;
  replacingConfigOption?: ConnectAndLoadSessionOptions["replacingConfigOption"];
  sessionId: string;
  pendingAgentSessionId: string | undefined;
  originalSessionId: string;
  originalAgentSessionId: string | undefined;
  originalAcpx: SessionRecord["acpx"];
  desiredModeId: string | undefined;
  desiredModelId: string | undefined;
  desiredConfigOptions: Record<string, string>;
  sessionModels: import("../../acp/client.js").SessionLoadResult["models"];
  timeoutMs?: number;
  verbose?: boolean;
  suppressWarnings?: boolean;
  authority?: AcpControlAuthority;
};

async function replaySessionPreferences(
  params: ReplaySessionPreferencesOptions,
): Promise<PreferenceReplayResult> {
  if (params.reusingLoadedSession) {
    return {
      modelReplay: { replayed: false },
      configReplay: { replayed: false },
    };
  }

  let modelReplay: ModelReplayResult = { replayed: false };
  let configReplay: ConfigReplayResult = { replayed: false };
  let modeMetadata: PreferenceReplayResult["modeMetadata"];
  const replay = createReplayState(params.authority);
  try {
    assertControlAuthority(replay.authority);
    modeMetadata = await replayDesiredMode({
      client: params.client,
      record: params.record,
      sessionId: params.sessionId,
      desiredModeId: params.createdFreshSession ? params.desiredModeId : undefined,
      replay,
      previousSessionId: params.originalSessionId,
      timeoutMs: params.timeoutMs,
      verbose: params.verbose,
    });
    const effectiveModels = resolveModeReplayModels(modeMetadata, params.sessionModels);
    const replacingModel = replacesModel(
      params.replacingConfigOption,
      effectiveModels,
      params.originalAcpx,
    );
    modelReplay = await replayDesiredModel({
      client: params.client,
      sessionId: params.sessionId,
      desiredModelId: replacingModel ? undefined : params.desiredModelId,
      replay,
      previousSessionId: params.originalSessionId,
      record: params.record,
      models: effectiveModels,
      createdFreshSession: params.createdFreshSession,
      timeoutMs: params.timeoutMs,
      verbose: params.verbose,
      suppressWarnings: params.suppressWarnings,
    });
    configReplay = await replayDesiredConfigOptions({
      client: params.client,
      record: params.record,
      sessionId: params.sessionId,
      desiredConfigOptions: replacingModel ? {} : params.desiredConfigOptions,
      replay,
      acceptedConfigOptions: modelReplay.configOptions,
      replacingConfigOption: replacingModel ? undefined : params.replacingConfigOption,
      previousSessionId: params.originalSessionId,
      timeoutMs: params.timeoutMs,
      verbose: params.verbose,
    });
  } catch (error) {
    settleFailedReplay(params, replay, error);
    if (params.verbose) {
      process.stderr.write(`[acpx] ${formatErrorMessage(error)}\n`);
    }
    throw error;
  }

  params.record.acpSessionId = params.sessionId;
  reconcileAgentSessionId(params.record, params.pendingAgentSessionId);
  return { modelReplay, configReplay, modeMetadata };
}

function settleFailedReplay(
  params: ReplaySessionPreferencesOptions,
  replay: ReplayState,
  error: unknown,
): void {
  if (replay.isAuthorityFailure(error) && replay.acknowledged) {
    // Earlier replies already changed the agent; retain their accepted state.
    params.record.acpSessionId = params.sessionId;
    reconcileAgentSessionId(params.record, params.pendingAgentSessionId);
  } else {
    restoreOriginalSessionState({
      record: params.record,
      sessionId: params.originalSessionId,
      agentSessionId: params.originalAgentSessionId,
    });
    params.record.acpx = cloneSessionAcpxState(params.originalAcpx);
  }
}

type RuntimeSessionLoadState = {
  sessionId: string;
  pendingAgentSessionId: string | undefined;
  sessionModels: import("../../acp/client.js").SessionLoadResult["models"];
  configOptionsPresent: boolean;
  legacyModelMetadataPresent: boolean;
  resumed: boolean;
  createdFreshSession: boolean;
  loadError?: string;
};

async function loadOrCreateRuntimeSession(params: {
  client: AcpClient;
  record: SessionRecord;
  reusingLoadedSession: boolean;
  sameSessionOnly: boolean;
  timeoutMs?: number;
  authority?: AcpControlAuthority;
}): Promise<RuntimeSessionLoadState> {
  if (params.reusingLoadedSession) {
    return {
      sessionId: params.record.acpSessionId,
      pendingAgentSessionId: params.record.agentSessionId,
      sessionModels: undefined,
      configOptionsPresent: false,
      legacyModelMetadataPresent: false,
      resumed: true,
      createdFreshSession: false,
    };
  }

  if (params.client.supportsResumeSession()) {
    return await loadRuntimeSession(params, true);
  }

  if (params.client.supportsLoadSession()) {
    return await loadRuntimeSession(params, false);
  }

  if (params.sameSessionOnly) {
    throw makeSessionResumeRequiredError({
      record: params.record,
      reason: "agent does not support session/resume or session/load",
    });
  }

  return await createFreshRuntimeSession(
    params.client,
    params.record,
    params.timeoutMs,
    params.authority,
  );
}

async function loadRuntimeSession(
  params: {
    client: AcpClient;
    record: SessionRecord;
    sameSessionOnly: boolean;
    timeoutMs?: number;
    authority?: AcpControlAuthority;
  },
  resume: boolean,
): Promise<RuntimeSessionLoadState> {
  try {
    const loadResult = await withTimeout(
      resume
        ? params.client.resumeSession(
            params.record.acpSessionId,
            params.record.cwd,
            params.authority,
          )
        : params.client.loadSessionWithOptions(params.record.acpSessionId, params.record.cwd, {
            suppressReplayUpdates: true,
            ...(params.authority ? { authority: params.authority } : {}),
          }),
      params.timeoutMs,
    );
    reconcileAgentSessionId(params.record, loadResult.agentSessionId);
    applyConfigOptionsToRecord(params.record, loadResult);
    return {
      sessionId: params.record.acpSessionId,
      pendingAgentSessionId: params.record.agentSessionId,
      sessionModels: loadResult.models,
      configOptionsPresent: loadResult.configOptionsPresent,
      legacyModelMetadataPresent: loadResult.legacyModelMetadataPresent,
      resumed: true,
      createdFreshSession: false,
    };
  } catch (error) {
    rethrowCancelledLoad(error, params.authority);
    return await recoverRuntimeSessionLoadFailure(params, error);
  }
}

function rethrowCancelledLoad(error: unknown, authority?: AcpControlAuthority): void {
  const signal = authority?.signal;
  if (signal?.aborted && (error instanceof AgentDisconnectedError || error === signal.reason)) {
    throw signal.reason;
  }
}

async function recoverRuntimeSessionLoadFailure(
  params: {
    client: AcpClient;
    record: SessionRecord;
    sameSessionOnly: boolean;
    timeoutMs?: number;
    authority?: AcpControlAuthority;
  },
  error: unknown,
): Promise<RuntimeSessionLoadState> {
  const loadError = formatErrorMessage(error);
  if (params.sameSessionOnly) {
    throw makeSessionResumeRequiredError({
      record: params.record,
      reason: loadError,
      cause: error,
    });
  }
  if (!shouldFallbackToNewSession(error, params.record)) {
    throw error;
  }
  return {
    ...(await createFreshRuntimeSession(
      params.client,
      params.record,
      params.timeoutMs,
      params.authority,
    )),
    loadError,
  };
}

async function createFreshRuntimeSession(
  client: AcpClient,
  record: SessionRecord,
  timeoutMs: number | undefined,
  authority?: AcpControlAuthority,
): Promise<RuntimeSessionLoadState> {
  const createdSession = await withTimeout(client.createSession(record.cwd, authority), timeoutMs);
  applyConfigOptionsToRecord(record, createdSession);
  return {
    sessionId: createdSession.sessionId,
    pendingAgentSessionId: createdSession.agentSessionId,
    sessionModels: createdSession.models,
    configOptionsPresent: createdSession.configOptionsPresent,
    legacyModelMetadataPresent: createdSession.legacyModelMetadataPresent,
    resumed: false,
    createdFreshSession: true,
  };
}
